import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';

export const BenefitsSection = () => {
  const benefits = [
    {
      title: "You know exactly what you own and why you own it.",
      description: "When you understand the framework behind a position, market volatility becomes information instead of a panic trigger. You can evaluate what is happening and decide with clarity instead of reacting with fear."
    },
    {
      title: "You have clear criteria for when to reduce your risk.",
      description: "Without a framework, the question of when to sell is always emotional. With one, it becomes analytical. Charles's system gives you specific criteria for evaluating when a sector is rotating, when a position's thesis has changed, and when reducing exposure makes strategic sense."
    },
    {
      title: "You stop depending on advisors, media, or luck.",
      description: "Financial advisors are useful for estate planning. They are not designed to teach you how to evaluate opportunities yourself. Payne's Education is the alternative neither your advisor nor the financial media were ever going to offer you: a structured education that builds genuine competence."
    },
    {
      title: "You build the confidence that comes from understanding your own portfolio.",
      description: "Most market anxiety comes from uncertainty. When you understand why you own what you own and what would change your thesis, market movements become less threatening. Members consistently describe a shift from chronic anxiety about their accounts to a calmer, more analytical relationship with their portfolio."
    }
  ];

  return (
    <section 
      data-testid="benefits-section"
      className="section-padding px-8 md:px-12 bg-gray-50"
    >
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-2xl mb-16"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-[2px] bg-amber-600" />
            <p className="font-sans text-xs tracking-widest uppercase text-gray-400">
              What Changes
            </p>
          </div>
          
          <h2 className="font-editorial text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
            Less Anxiety. More Clarity.{' '}
            <span className="text-amber-600">Here Is What That Actually Looks Like.</span>
          </h2>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 gap-x-16 gap-y-10">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              data-testid={`benefit-card-${index}`}
              className="benefit-card group"
            >
              <div className="flex items-start gap-4 mb-3">
                <CheckCircle2 className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                <h3 className="font-editorial text-xl font-bold text-gray-900 group-hover:text-amber-600 transition-colors">
                  {benefit.title}
                </h3>
              </div>
              <p className="font-sans text-gray-600 leading-relaxed pl-10">
                {benefit.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
